import React, {useMemo, useRef} from "react";
import {
	getAspectRatioFromImage,
	getCssSizeValue,
	getMapKey,
	getPaddingsClassFromData,
	groupArrayBySize,
	isStringEmpty,
	precise
} from "@/utils/utils";
import GridContainerFiveColumns from "@/components/common/GridContainerFiveColumns";
import styled, {useTheme} from "styled-components";
import {BREAKPOINTS} from "@/styles/themeSettings";
import {IMAGE_FALLBACK} from "@/constants";
import Image from "@/components/common/Image";
import {gsap, useGSAP} from "@/utils/gsap";
import useColor from "@/hooks/useColor";
import ExternalLink from "@/components/common/Links/ExternalLink";
import HeaderDescriptionWithButton, {
	HEADER_DESCRIPTION_WITH_BUTTON_VARIANT
} from "@/components/common/HeaderDescriptionWithButton";
import {useRouter} from "next/router";

const Wrapper = styled.div``

const StyledContainer = styled(GridContainerFiveColumns)`
    background-color: ${({$background, theme}) => $background || theme.colors.white};

    @media (max-width: ${({theme}) => theme.breakpoints.tablet - 1}px) {
        grid-template-columns: repeat(2, 1fr);
    }
	padding-top: ${getCssSizeValue(120)};
`

const ItemWrapper = styled.div`
    display: flex;
    position: relative;
    flex-direction: column;
    justify-content: space-between;
    //outline: springgreen solid 1px;
    aspect-ratio: 293/240;
    padding: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)} ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};


    @media (min-width: ${({theme}) => theme.breakpoints.desktop || 1024}px) and (orientation: landscape) {
        margin-bottom: unset;
        padding: ${getCssSizeValue(24)} ${getCssSizeValue(16)};
        aspect-ratio: 427/280;
    }
`;

const LogoWrapper = styled.div`
    position: absolute;
    left: 50%;
    top: 50%;
    width: 90%;
    translate: -50% -50%;
    aspect-ratio: 16/9;
    clip-path: inset(0);
    pointer-events: none;

    > * {
        pointer-events: auto;
    }

    img {
        object-position: center;
    }

    @media (min-width: ${({theme}) => theme.breakpoints.tablet || 768}px) {
        width: 80%;
    }

    @media (min-width: ${({theme}) => theme.breakpoints.desktop || 1024}px) and (orientation: landscape) {
        width: 65%;
    }
`

const Link = styled(ExternalLink)`
    display: flex;
    height: 100%;
    width: 100%;
`

const NoLinkWrapper = styled.div`
    display: flex;
    height: 100%;
    width: 100%;
`

const PARAMS = [
	{
		start: {
			yPercent: 101,
		},
		middle: {
			yPercent: 0,
		},
		end: {
			xPercent: 101,
		},
	},
	{
		start: {
			xPercent: -101,
		},
		middle: {
			xPercent: 0,
		},
		end: {
			yPercent: -101,
		},
	},
	{
		start: {
			xPercent: 101,
		},
		middle: {
			xPercent: 0,
		},
		end: {
			yPercent: 101,
		},
	},
	{
		start: {
			yPercent: -101,
		},
		middle: {
			yPercent: 0,
		},
		end: {
			yPercent: 101,
		},
	},
	{
		start: {
			yPercent: -101,
		},
		middle: {
			yPercent: 0,
		},
		end: {
			xPercent: -101,
		},
	}
]

const BG_VARIANT = {
	DARK: 'dark',
	LIGHT: 'light',
	GRAY: 'gray'
}
const AnimatedItem = ({data, index = 0, delay: groupDelay = 0}) => {
	const router = useRouter()
	const theme = useTheme()
	const aspectRatio = getAspectRatioFromImage(data?.logo || {})
	const WrapperRef = useRef()
	const LogoRefs = useRef([])

	useGSAP(() => {
		const duration = 1
		const paramIndex = index % PARAMS.length //make sure it doesn't go overboard
		const params = PARAMS[paramIndex] || PARAMS[0]
		const tl = gsap.timeline({paused: true})
		LogoRefs.current.forEach((item) => {
			gsap.set(item, {
				...params.start,
			})
			tl.to(item, {
				duration,
				...params.middle,
			})
			tl.to(item, {
				delay: 2 * duration,
				duration,
				...params.end
			})
		})
		tl.repeat(-1)
		tl.play().delay(precise(groupDelay))

	}, {
		dependencies: [index, groupDelay, router.locale],
		revertOnUpdate: true,
		scope: WrapperRef.current
	});

	return (
		<ItemWrapper ref={WrapperRef}>
			{data.map((partner, index) => {
				const logo = partner?.logo || IMAGE_FALLBACK
				const key = getMapKey(partner, index)
				const url = partner?.websiteUrl || ''
				const isLink = isStringEmpty(url)
				return (
					<LogoWrapper key={key} $aspectRatio={aspectRatio}>
						<Link
							ref={(ref) => LogoRefs.current[index] = ref}
							newCard={!isLink ? undefined : 'yes'}
							href={!isLink ? undefined : url}
							as={!isLink ? 'div' : ExternalLink}
						>
							<Image
								src={logo.url}
								alt={logo.alt || 'SinglePartner Logo'}
								className="partner-logo"
								fit="contain"
								sizes={`(min-width: ${theme.breakpoints[BREAKPOINTS.DESKTOP]}px) 22vw, 90vw`}
							/>
						</Link>
					</LogoWrapper>
				)
			})}
		</ItemWrapper>
	)
}

const bgColors = {
	[BG_VARIANT.DARK]: 'richBlue',
	[BG_VARIANT.LIGHT]: 'white',
	[BG_VARIANT.GRAY]: 'platinum',
}

export default function AnimatedVariant({data}) {
	const paddings = getPaddingsClassFromData(data)
	const partnersGroups = useMemo(() => {
		const source = data?.partners || []
		const size = Math.ceil((source?.length || 1)/5)
		return groupArrayBySize(source, size)
	}, [data])
	const backgroundVariant = data?.animatedVariantBackground || ''
	const bgColor = useColor(bgColors[backgroundVariant] || bgColors[BG_VARIANT.LIGHT])
	const header = data?.header || ''
	const description = data?.description || ''
	const button = data?.button || null

	const variant = backgroundVariant === BG_VARIANT.DARK ? HEADER_DESCRIPTION_WITH_BUTTON_VARIANT.DARK : HEADER_DESCRIPTION_WITH_BUTTON_VARIANT.LIGHT

	return (
		<Wrapper className={`${paddings}`}>
			<HeaderDescriptionWithButton
				header={header}
				description={description}
				button={button}
				variant={variant}
			/>
			<StyledContainer $background={bgColor} className={`no-margin padding `}>
				{partnersGroups.map((group, index, arr) => (
					<AnimatedItem
						key={getMapKey(group, index)}
						data={group}
						index={index}
						delay={index * 0.15}
					/>
				))}
			</StyledContainer>
		</Wrapper>
	);
}
